/**
 * Picks a watch type's `properties` from the fixed vocabulary
 * (config/watchTypeProperties.ts) instead of free text.
 *
 * Three rules:
 *  - Legacy flags are live and tickable — every consumer reads them today.
 *  - Buckets are tickable but marked inert until the bucket registry lands, so a
 *    curator can set them up ahead of time without changing any behaviour.
 *  - A stored value NOT in the vocabulary is shown as unknown and kept until the
 *    curator removes it. Silently dropping it would hide the typo this picker
 *    exists to expose, and could strip a flag some consumer still reads.
 */
import {
  WATCH_TYPE_PROPERTIES,
  unknownWatchTypeProperties,
  type WatchTypePropertyDef,
  type WatchTypePropertyGroup,
} from '../../config/watchTypeProperties';

const GROUP_LABEL: Record<WatchTypePropertyGroup, string> = {
  face: 'Face',
  wear: 'How it is worn',
  case: 'Case & controls',
  movement: 'Movement',
};
const GROUP_ORDER: WatchTypePropertyGroup[] = ['face', 'wear', 'case', 'movement'];

interface Props {
  values: string[];
  onChange: (values: string[]) => void;
}

export function WatchTypePropertiesPicker({ values, onChange }: Props) {
  const selected = new Set(values);
  const unknown = unknownWatchTypeProperties(values);

  const toggle = (id: string) =>
    onChange(selected.has(id) ? values.filter((v) => v !== id) : [...values, id]);

  const section = (kind: WatchTypePropertyDef['kind']) =>
    GROUP_ORDER.map((group) => {
      const defs = WATCH_TYPE_PROPERTIES.filter((p) => p.kind === kind && p.group === group);
      if (defs.length === 0) return null;
      return (
        <div key={`${kind}-${group}`} className="mb-2">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {GROUP_LABEL[group]}
          </div>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {defs.map((p) => (
              <label key={p.id} className="flex items-start gap-2 rounded px-1 py-0.5 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={selected.has(p.id)}
                  onChange={() => toggle(p.id)}
                  aria-label={p.label}
                />
                <span className="text-sm">
                  <span className="text-gray-800">{p.label}</span>{' '}
                  <code className="text-[10px] text-gray-400">{p.id}</code>
                  <span className="block text-xs text-gray-500">{p.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      );
    });

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1 text-sm font-medium text-gray-700">Flags in use today</div>
        <p className="mb-2 text-xs text-gray-500">
          Read by the ref editor, the brand portal, the app and the AI enrichment prompt.
        </p>
        {section('legacy')}
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
        <div className="mb-1 text-sm font-medium text-gray-700">Property buckets</div>
        <p className="mb-2 text-xs text-gray-500">
          The physical properties of this kind of watch. Safe to tick now: they take effect when the
          prompt and editor move onto buckets, and the flags above keep working until then.
        </p>
        {section('bucket')}
      </div>

      {unknown.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-2" role="alert">
          <div className="text-sm font-medium text-amber-900">Not in the vocabulary</div>
          <p className="mb-1 text-xs text-amber-800">
            Probably a typo or a retired flag. Nothing reads it, so it switches nothing on. Remove it
            unless you know it is needed.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {unknown.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 text-sm text-amber-900"
              >
                <code>{v}</code>
                <button
                  type="button"
                  aria-label={`Remove ${v}`}
                  className="text-amber-600 hover:text-amber-800"
                  onClick={() => onChange(values.filter((x) => x !== v))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
